import { useCallback, useMemo, useRef, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import {
  UploadCloud,
  FileAudio,
  Copy,
  Download,
  AlertCircle,
  Loader2,
  RotateCcw,
  Waves,
  CheckCircle2,
  Users,
  FileText,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// NOTE: this ends up readable in the compiled browser bundle — it's not a
// secret, just a crude anti-bot/anti-abuse gate. See README.md.
const API_KEY = process.env.REACT_APP_API_KEY;

// Native Whisper formats
const AUDIO_NATIVE = ["mp3", "wav", "m4a", "webm", "mp4", "mpeg", "mpga"];
// Extra audio containers (server transcodes via ffmpeg)
// NOTE: flac intentionally still excluded — kept in sync with backend/server.py
// EXTRA_AUDIO_EXTS (see memory/PRD.md backlog for why). ogg was excluded for
// the same reason but is re-enabled now that the backend independently
// verifies real stream content (_verify_media_stream) before transcoding.
const AUDIO_EXTRA = [
  "ogg", "oga", "opus", "aac", "wma",
  "aiff", "aif", "aifc", "amr", "ac3", "au", "caf",
  "3ga", "voc", "mka", "mp2",
];
// Video containers (server extracts audio track)
const VIDEO_EXT = [
  "mov", "avi", "mkv", "wmv", "flv", "3gp", "3g2",
  "m4v", "mpg", "mpe", "vob", "ogv", "mts", "m2ts",
  "ts", "asf", "f4v",
];
// DVR/security-camera recordings — best-effort, not guaranteed (see
// backend/server.py DVR_EXTS comment); .dav gets a distinct, actionable
// error message from the backend if its codec can't be decoded.
const DVR_EXT = ["dav"];
const ALLOWED_EXT = [...AUDIO_NATIVE, ...AUDIO_EXTRA, ...VIDEO_EXT, ...DVR_EXT];
const VIDEO_SET = new Set([...VIDEO_EXT, ...DVR_EXT]);
const ACCEPT_ATTR = "audio/*,video/*," + ALLOWED_EXT.map((e) => `.${e}`).join(",");
// Single source of truth: backend/server.py MAX_UPLOAD_SIZE (500 MB). Keep in
// sync by hand — frontend and backend are separate apps with no shared package.
const MAX_SIZE_MB = 500;

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const getExt = (name = "") => (name.includes(".") ? name.split(".").pop().toLowerCase() : "");

export default function Transcriber() {
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | uploading | done | error
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null); // {text, diarized_text, language, filename, size_bytes}
  const [error, setError] = useState(null);
  const [showDiarized, setShowDiarized] = useState(true);
  const [qualityMode, setQualityMode] = useState("standard"); // "standard" | "precise"
  const [enableDiarization, setEnableDiarization] = useState(false); // opt-in, slow
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const validateAndSetFile = useCallback((f) => {
    if (!f) return;
    const ext = getExt(f.name);
    if (!ALLOWED_EXT.includes(ext)) {
      toast.error("Desteklenmeyen dosya formatı", {
        description: `.${ext || "?"} desteklenmiyor. Kabul edilen: ${ALLOWED_EXT.join(", ")}`,
      });
      return;
    }
    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error("Dosya çok büyük", {
        description: `Maksimum ${MAX_SIZE_MB} MB. Yüklenen: ${formatBytes(f.size)}`,
      });
      return;
    }
    setFile(f);
    setResult(null);
    setError(null);
    setStatus("idle");
    setProgress(0);
  }, []);

  const onSelect = (e) => {
    const f = e.target.files?.[0];
    validateAndSetFile(f);
    // Reset input value so selecting same file re-triggers change
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    validateAndSetFile(f);
  };

  const startTranscription = async () => {
    if (!file) return;
    setStatus("uploading");
    setProgress(0);
    setError(null);
    setResult(null);

    const form = new FormData();
    form.append("file", file);
    form.append("language", "tr");
    form.append("quality_mode", qualityMode);
    form.append("enable_diarization", enableDiarization ? "true" : "false");

    try {
      const res = await axios.post(`${API}/transcribe`, form, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(API_KEY ? { "X-API-Key": API_KEY } : {}),
        },
        onUploadProgress: (evt) => {
          if (evt.total) {
            // Upload phase up to 60%, remainder simulates transcode + chunked transcription
            const uploadPct = Math.round((evt.loaded / evt.total) * 60);
            setProgress(uploadPct);
          }
        },
        timeout: 30 * 60 * 1000, // 30 min for very large files
      });
      // Simulate processing tick to 95, then complete
      setProgress(95);
      setResult(res.data);
      setStatus("done");
      setProgress(100);
      toast.success("Transkripsiyon tamamlandı");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Bilinmeyen bir hata oluştu.";
      setError(detail);
      setStatus("error");
      toast.error("Transkripsiyon başarısız", { description: detail });
    }
  };

  const resetAll = () => {
    setFile(null);
    setResult(null);
    setError(null);
    setStatus("idle");
    setProgress(0);
  };

  const activeText = useMemo(() => {
    if (!result) return "";
    if (showDiarized && result.diarized_text) return result.diarized_text;
    return result.text || "";
  }, [result, showDiarized]);

  const hasDiarization = Boolean(result?.diarized_text);

  const copyText = async () => {
    if (!activeText) return;
    try {
      await navigator.clipboard.writeText(activeText);
      toast.success("Panoya kopyalandı");
    } catch {
      toast.error("Kopyalanamadı");
    }
  };

  const downloadTxt = () => {
    if (!activeText) return;
    const blob = new Blob([activeText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const base = (result.filename || "transkripsiyon").replace(/\.[^.]+$/, "");
    const suffix = showDiarized && hasDiarization ? "-konusmacilar" : "";
    a.download = `${base}${suffix}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("İndirildi", { description: `${base}${suffix}.txt` });
  };

  const wordCount = useMemo(() => {
    if (!activeText) return 0;
    return activeText.trim().split(/\s+/).filter(Boolean).length;
  }, [activeText]);

  const charCount = activeText.length || 0;

  const speakerCount = useMemo(() => {
    if (!result?.diarized_text) return 0;
    const matches = result.diarized_text.match(/(\d+)\.\s*kişi\s*:/gi) || [];
    const seen = new Set(matches.map((m) => m.match(/\d+/)[0]));
    return seen.size;
  }, [result]);

  return (
    <div className="min-h-screen w-full bg-[#FAFAFA] text-[#111827]">
      {/* Header */}
      <header className="border-b border-black/10 bg-[#FAFAFA]/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-black text-white flex items-center justify-center hard-shadow-sm">
              <Waves className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <div className="leading-tight">
              <div className="font-display font-black text-lg tracking-tight" data-testid="app-title">
                SesDeşifre
              </div>
              <div className="tech-label">Ses → Metin Dönüştürücü</div>
            </div>
          </div>
          <div
            className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 border border-black/20 bg-white"
            data-testid="feature-badge"
          >
            <span className="w-1.5 h-1.5 bg-[#002FA7]" />
            <span className="tech-label !text-[10px] !text-black">
              Düşük Frekans Optimizasyonu · Aktif
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-20">
        {/* Hero */}
        <section className="mb-10">
          <div className="tech-label mb-4">V1 · Whisper Motoru</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
            Kalitesiz sesleri
            <br />
            <span className="inline-block bg-black text-white px-2 py-0.5">temiz metne</span> dönüştürün.
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-gray-600">
            Düşük frekanslı, gürültülü veya kalitesiz kayıtları yükleyin. Sistem,
            konuşmayı yüksek doğrulukla Türkçe metne çevirir. Hesap gerekmez.
          </p>
        </section>

        {/* Dropzone */}
        <AnimatePresence mode="wait">
          {status === "idle" && !file && (
            <motion.div
              key="dropzone"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <label
                htmlFor="audio-input"
                data-testid="upload-dropzone"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    inputRef.current?.click();
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={`focus-ring block cursor-pointer border-2 border-dashed p-12 md:p-16 text-center bg-white transition-all ${
                  dragOver
                    ? "border-black bg-black/[0.04]"
                    : "border-black/25 hover:border-black hover:bg-black/[0.03]"
                }`}
              >
                <div className="mx-auto w-14 h-14 bg-black text-white flex items-center justify-center mb-6">
                  <UploadCloud className="w-7 h-7" />
                </div>
                <div className="font-display font-bold text-xl md:text-2xl tracking-tight mb-2">
                  Ses veya video dosyasını buraya sürükleyin
                </div>
                <div className="text-sm text-gray-600 mb-6">
                  veya seçmek için tıklayın
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <div className="inline-flex items-center gap-2 border border-black/15 bg-[#FAFAFA] px-3 py-1.5">
                    <span className="tech-label !text-[10px]">Ses</span>
                    <span className="font-mono text-xs">
                      mp3 · wav · m4a · opus · ogg · aac …
                    </span>
                  </div>
                  <div className="inline-flex items-center gap-2 border border-black/15 bg-[#FAFAFA] px-3 py-1.5">
                    <span className="tech-label !text-[10px]">Video</span>
                    <span className="font-mono text-xs">
                      mp4 · mov · mkv · avi · webm · flv …
                    </span>
                  </div>
                  <div className="inline-flex items-center gap-2 border border-black/15 bg-[#FAFAFA] px-3 py-1.5">
                    <span className="tech-label !text-[10px]">DVR</span>
                    <span className="font-mono text-xs">dav</span>
                  </div>
                </div>
                <div className="mt-3 tech-label !text-[10px]">
                  Maks · {MAX_SIZE_MB} MB · Videodan ses otomatik ayıklanır · Büyükler parçalanır
                </div>
                <input
                  ref={inputRef}
                  id="audio-input"
                  data-testid="audio-input"
                  type="file"
                  accept={ACCEPT_ATTR}
                  className="hidden"
                  onChange={onSelect}
                />
              </label>
            </motion.div>
          )}

          {/* Selected file / Uploading */}
          {file && status !== "done" && (
            <motion.div
              key="selected"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="bg-white border border-black/10 p-6 md:p-8 hard-shadow-sm"
              data-testid="selected-file-panel"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 min-w-0">
                  <div className="w-11 h-11 bg-black text-white flex items-center justify-center flex-shrink-0">
                    <FileAudio className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div
                      className="font-display font-bold text-base md:text-lg truncate"
                      data-testid="selected-filename"
                    >
                      {file.name}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
                      <span className="tech-label">Boyut · {formatBytes(file.size)}</span>
                      <span className="tech-label">
                        Format · .{getExt(file.name)}
                      </span>
                      {VIDEO_SET.has(getExt(file.name)) && (
                        <span className="tech-label !text-[#002FA7]">
                          Video · Ses ayıklanacak
                        </span>
                      )}
                      {file.size > 25 * 1024 * 1024 && (
                        <span className="tech-label !text-[#002FA7]">
                          Otomatik parçalama · Aktif
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {status === "idle" && (
                  <button
                    onClick={resetAll}
                    className="tech-label hover:text-black underline underline-offset-4 decoration-black/30 hover:decoration-black"
                    data-testid="change-file-btn"
                  >
                    Değiştir
                  </button>
                )}
              </div>

              {/* Progress bar */}
              {status === "uploading" && (
                <div className="mt-8" data-testid="progress-panel">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-[#002FA7]" />
                      <span className="tech-label !text-black">
                        Ses işleniyor · Gürültü azaltma & düşük frekans iyileştirme
                      </span>
                    </div>
                    <span className="font-mono text-sm" data-testid="progress-percent">
                      {progress}%
                    </span>
                  </div>
                  <div className="h-2 w-full bg-black/10 overflow-hidden">
                    <motion.div
                      className="h-full bg-[#002FA7]"
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                    />
                  </div>
                </div>
              )}

              {status === "error" && (
                <div className="mt-6 border border-[#FF3B30] bg-[#FFF5F4] p-4 flex items-start gap-3" data-testid="error-panel">
                  <AlertCircle className="w-5 h-5 text-[#FF3B30] flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <div className="font-semibold text-[#B32218]">Bir hata oluştu</div>
                    <div className="text-gray-700 mt-1 break-words">{error}</div>
                  </div>
                </div>
              )}

              {status === "idle" && (
                <div className="mt-8" data-testid="quality-mode-panel">
                  <div className="tech-label mb-2">Mod</div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setQualityMode("standard")}
                      data-testid="quality-mode-standard-btn"
                      className={`px-4 py-2 border text-sm font-medium transition-colors ${
                        qualityMode === "standard"
                          ? "bg-black text-white border-black"
                          : "border-black/20 text-gray-700 hover:border-black/50"
                      }`}
                    >
                      Hızlı (standart)
                    </button>
                    <button
                      type="button"
                      onClick={() => setQualityMode("precise")}
                      data-testid="quality-mode-precise-btn"
                      className={`px-4 py-2 border text-sm font-medium transition-colors ${
                        qualityMode === "precise"
                          ? "bg-black text-white border-black"
                          : "border-black/20 text-gray-700 hover:border-black/50"
                      }`}
                    >
                      Hassas (yavaş, gürültülü/zor kayıtlar için)
                    </button>
                  </div>
                  <div className="mt-2 text-xs text-gray-600 max-w-prose">
                    Hassas mod, düşük kaliteli/gürültülü kayıtlarda bazı durumlarda
                    daha doğru sonuç verir ama ~3x daha uzun sürer (bkz.{" "}
                    <span className="font-mono">BENCHMARK.md</span>). Net/temiz
                    kayıtlarda iki mod da aynı sonucu üretebilir.
                  </div>

                  <label
                    className="mt-5 flex items-start gap-2.5 cursor-pointer select-none"
                    data-testid="enable-diarization-toggle"
                  >
                    <input
                      type="checkbox"
                      checked={enableDiarization}
                      onChange={(e) => setEnableDiarization(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-black cursor-pointer"
                    />
                    <span>
                      <span className="text-sm font-medium text-black">
                        Konuşmacıları ayır (yavaş)
                      </span>
                      <span className="block mt-0.5 text-xs text-gray-600 max-w-prose">
                        Kim ne zaman konuştu bilgisini zaman damgalarıyla üretir.
                        Ekstra bir işlem adımı olduğu için transkripsiyonu
                        belirgin şekilde yavaşlatır — varsayılan kapalı.
                      </span>
                    </span>
                  </label>
                </div>
              )}

              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  onClick={startTranscription}
                  disabled={status === "uploading"}
                  className="bg-black text-white px-6 py-3 font-medium hover:bg-gray-800 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="start-transcribe-btn"
                >
                  {status === "uploading" ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Dönüştürülüyor…
                    </>
                  ) : (
                    <>
                      <Waves className="w-4 h-4" />
                      Metne Dönüştür
                    </>
                  )}
                </button>
                {status !== "uploading" && (
                  <button
                    onClick={resetAll}
                    className="border border-black text-black px-6 py-3 font-medium hover:bg-gray-100 transition-colors flex items-center gap-2"
                    data-testid="cancel-btn"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Vazgeç
                  </button>
                )}
              </div>
            </motion.div>
          )}

          {/* Result */}
          {status === "done" && result && (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white border border-black/10 p-6 md:p-8 hard-shadow"
              data-testid="result-panel"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-[#002FA7] text-white flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-display font-bold text-lg tracking-tight">
                      Transkripsiyon Tamamlandı
                    </div>
                    <div className="tech-label mt-0.5">
                      {wordCount} kelime · {charCount} karakter
                      {result.chunks > 1 && ` · ${result.chunks} parça`}
                      {hasDiarization && speakerCount > 0 && ` · ${speakerCount} konuşmacı`}
                    </div>
                  </div>
                </div>
                <button
                  onClick={resetAll}
                  className="tech-label hover:text-black underline underline-offset-4 decoration-black/30 hover:decoration-black"
                  data-testid="new-file-btn"
                >
                  ↺ Yeni Dosya
                </button>
              </div>

              {hasDiarization && (
                <div
                  className="mt-5 inline-flex border border-black/15 bg-[#FAFAFA]"
                  data-testid="view-toggle"
                >
                  <button
                    onClick={() => setShowDiarized(true)}
                    className={`px-4 py-2 tech-label !text-[10px] flex items-center gap-2 transition-colors ${
                      showDiarized
                        ? "bg-black text-white"
                        : "text-gray-700 hover:bg-black/5"
                    }`}
                    data-testid="toggle-diarized-btn"
                  >
                    <Users className="w-3.5 h-3.5" />
                    Konuşmacılara Ayrılmış
                  </button>
                  <button
                    onClick={() => setShowDiarized(false)}
                    className={`px-4 py-2 tech-label !text-[10px] flex items-center gap-2 transition-colors border-l border-black/15 ${
                      !showDiarized
                        ? "bg-black text-white"
                        : "text-gray-700 hover:bg-black/5"
                    }`}
                    data-testid="toggle-raw-btn"
                  >
                    <FileText className="w-3.5 h-3.5" />
                    Düz Metin
                  </button>
                </div>
              )}

              <div
                aria-live="polite"
                data-testid="transcription-text"
                className="mt-6 leading-relaxed text-[15px] text-gray-800 max-h-[52vh] overflow-y-auto custom-scrollbar text-left whitespace-pre-wrap break-words border-l-2 border-black/10 pl-4"
              >
                {activeText ? (
                  showDiarized && hasDiarization ? (
                    <DiarizedText text={activeText} />
                  ) : (
                    activeText
                  )
                ) : (
                  <span className="text-gray-500 italic">
                    (Ses içerisinde metne çevrilebilir konuşma bulunamadı.)
                  </span>
                )}
              </div>

              {result.speaker_timeline && (
                <div className="mt-6 pt-6 border-t border-black/10" data-testid="speaker-timeline-panel">
                  <div className="tech-label mb-3 flex items-center gap-2">
                    <Users className="w-3.5 h-3.5" />
                    Konuşmacı Zaman Çizelgesi
                  </div>
                  <div className="max-h-[40vh] overflow-y-auto custom-scrollbar pl-4 border-l-2 border-black/10">
                    <SpeakerTimeline text={result.speaker_timeline} />
                  </div>
                </div>
              )}

              <div className="flex flex-wrap justify-end gap-3 mt-6 border-t border-black/10 pt-5">
                <button
                  onClick={copyText}
                  className="border border-black text-black px-5 py-2.5 font-medium hover:bg-gray-100 transition-colors flex items-center gap-2"
                  data-testid="copy-btn"
                >
                  <Copy className="w-4 h-4" />
                  Panoya Kopyala
                </button>
                <button
                  onClick={downloadTxt}
                  className="bg-black text-white px-5 py-2.5 font-medium hover:bg-gray-800 transition-colors flex items-center gap-2"
                  data-testid="download-btn"
                >
                  <Download className="w-4 h-4" />
                  TXT Olarak İndir
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Info footer */}
        <section className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="info-section">
          {[
            {
              n: "01",
              t: "Kalitesiz Ses",
              d: "Düşük frekans, gürültü ve fısıltı içeren kayıtları destekler.",
            },
            {
              n: "02",
              t: "Çoklu Format",
              d: "Ses (mp3, wav, opus, ogg, aac…), video (mp4, mov, mkv…) ve DVR kayıtları (.dav) — hepsi otomatik.",
            },
            {
              n: "03",
              t: "Gizlilik",
              d: "Dosyalar yalnızca transkripsiyon için işlenir, saklanmaz.",
            },
          ].map((x) => (
            <div key={x.n} className="border-t-2 border-black pt-4">
              <div className="tech-label">{x.n}</div>
              <div className="font-display font-bold text-lg mt-1">{x.t}</div>
              <div className="text-sm text-gray-600 mt-2 leading-relaxed">{x.d}</div>
            </div>
          ))}
        </section>
      </main>

      <footer className="border-t border-black/10 py-6 mt-10">
        <div className="max-w-5xl mx-auto px-4 md:px-6 flex flex-wrap justify-between items-center gap-2">
          <div className="tech-label">SesDeşifre · Whisper v1</div>
          <div className="tech-label">© {new Date().getFullYear()}</div>
        </div>
      </footer>
    </div>
  );
}


// Renders diarized text: each "N. kişi:" prefix becomes a labeled block with
// a distinct color per speaker.
const SPEAKER_COLORS = [
  "#002FA7", // Klein blue
  "#B32218", // deep red
  "#0F766E", // teal
  "#7C2D12", // burnt orange
  "#4C1D95", // deep purple
  "#365314", // olive
];

function DiarizedText({ text }) {
  const lines = text
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean);

  return (
    <div className="space-y-4">
      {lines.map((line, i) => {
        const match = line.match(/^(\d+)\.\s*kişi\s*:\s*(.*)$/i);
        if (!match) {
          return (
            <p key={i} className="text-gray-800">
              {line}
            </p>
          );
        }
        const num = parseInt(match[1], 10);
        const body = match[2];
        const color = SPEAKER_COLORS[(num - 1) % SPEAKER_COLORS.length];
        return (
          <div key={i} className="flex gap-3">
            <div className="flex-shrink-0 pt-0.5">
              <span
                className="tech-label !text-[10px] !text-white px-2 py-1 inline-block"
                style={{ backgroundColor: color }}
              >
                {num}. kişi
              </span>
            </div>
            <p className="text-gray-800 flex-1 leading-relaxed">{body}</p>
          </div>
        );
      })}
    </div>
  );
}

// Renders the opt-in speaker_timeline field — backend/server.py
// _format_speaker_timeline's "Speaker {n}\n{start:.2f}\n{text}\n{end:.2f}"
// blocks, repeated. Distinct from DiarizedText above: this is timestamped
// (0-based "Speaker N", real start/end seconds per turn), that one is the
// older, un-timestamped "N. kişi:" format (diarized_text).
// Blocks are parsed by scanning for "Speaker N" / timestamp header lines
// rather than assuming a fixed 4-line stride — a rigid stride previously
// misaligned (and could silently swap start/end) if a block's body ever
// spanned more than one line. See CLAUDE.md "Bilinen Kritik Sorunlar" for
// the backend regression this was found alongside.
const TIMESTAMP_LINE = /^\d+\.\d{2}$/;
const SPEAKER_HEADER_LINE = /^Speaker\s+(\d+)$/i;

function SpeakerTimeline({ text }) {
  const lines = text.split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const headerMatch = lines[i].match(SPEAKER_HEADER_LINE);
    if (!headerMatch || !TIMESTAMP_LINE.test(lines[i + 1] || "")) {
      i += 1;
      continue;
    }
    const start = lines[i + 1];
    let j = i + 2;
    while (j < lines.length && !TIMESTAMP_LINE.test(lines[j])) {
      j += 1;
    }
    if (j >= lines.length) break; // no closing timestamp found — stop
    blocks.push({
      num: parseInt(headerMatch[1], 10),
      start,
      body: lines.slice(i + 2, j).join("\n"),
      end: lines[j],
    });
    i = j + 1;
  }

  return (
    <div className="space-y-4">
      {blocks.map((b, i) => {
        const color = SPEAKER_COLORS[b.num % SPEAKER_COLORS.length];
        return (
          <div key={i} className="flex gap-3">
            <div className="flex-shrink-0 pt-0.5 flex flex-col items-start gap-1 w-24">
              <span
                className="tech-label !text-[10px] !text-white px-2 py-1 inline-block"
                style={{ backgroundColor: color }}
              >
                Speaker {b.num}
              </span>
              <span className="font-mono text-[10px] text-gray-500">
                {b.start}s–{b.end}s
              </span>
            </div>
            <p className="text-gray-800 flex-1 leading-relaxed">{b.body}</p>
          </div>
        );
      })}
    </div>
  );
}
